import { Ref } from 'react';
import { VirtuosoHandle, VirtuosoProps, ListRange } from 'react-virtuoso';
import { UnpackArray, InfiniteQueryResult, CommonHTMLAttributes } from '@frontend/types';
import { PaginatedList, EmptyStateConfig, RenderListItemProps, PaginatedListRefType } from '../paginated-list';

type InfiniteListData = Record<'rows', any>;

type PaginatedListProps<T extends InfiniteListData> = {
  endOfListText?: React.ReactNode | string;
  infiniteQueryProps: InfiniteQueryResult<T>;
  offsetHeightProp?: number;
  overscan?: number;
  isReverseInfinite?: boolean;
  limit?: number;
  height?: number | string;
  emptyStateConfig?: EmptyStateConfig;
  skeletonLoader?: React.ReactNode;
  errorMessage?: string;
  errorState?: React.ReactNode;
  renderListItem: (args: RenderListItemProps<UnpackArray<T['rows']>>) => React.ReactNode;
  paginatedListRef?: Ref<PaginatedListRefType>;
  atBottomStateChange?: VirtuosoProps<T, any>['atBottomStateChange'];
  virtuosoRef?: Ref<VirtuosoHandle>;
  initialTopMostItemIndex?: number;
  /**
   * Callback that is called when the visible range of items changes.
   * Useful for tracking scroll position for persistence.
   */
  rangeChanged?: (range: ListRange) => void;
} & CommonHTMLAttributes;

export const InfinitePaginatedList = <T extends InfiniteListData>({
  endOfListText = '🎉 You have reached the end',
  infiniteQueryProps,
  skeletonLoader,
  overscan = 10,
  isReverseInfinite,
  renderListItem,
  emptyStateConfig,
  limit,
  errorMessage,
  paginatedListRef,
  atBottomStateChange,
  virtuosoRef,
  initialTopMostItemIndex,
  rangeChanged,
  ...rest
}: PaginatedListProps<T>) => {
  const { hasNextPage, isFetchingNextPage, fetchNextPage, status, data } = infiniteQueryProps;
  const isLoading = status === 'loading';
  const isError = status === 'error';
  const list = data ? data.pages.flatMap(({ rows }) => rows) : [];

  return (
    <PaginatedList
      listData={list as UnpackArray<T['rows']>[]}
      renderListItem={renderListItem}
      skeletonLoader={skeletonLoader}
      hasNextPage={hasNextPage}
      isError={isError}
      isLoading={isLoading}
      isReverseInfinite={isReverseInfinite}
      isFetchingNextPage={isFetchingNextPage}
      overscanCount={overscan}
      loadMore={fetchNextPage}
      limit={limit}
      errorMessage={errorMessage}
      emptyStateConfig={emptyStateConfig}
      endOfListText={endOfListText}
      paginatedListRef={paginatedListRef}
      atBottomStateChange={atBottomStateChange}
      virtuosoRef={virtuosoRef}
      initialTopMostItemIndex={initialTopMostItemIndex}
      rangeChanged={rangeChanged}
      {...rest}
    />
  );
};
